/** Liste des encaissements — actions sur la ligne. */
import { useCallback, useEffect, useMemo, useState } from "react";
import { facturxFetch } from "../api/client";
import { PageHeader } from "../components/PageHeader";
import { ListPager, usePagedRows } from "../components/ListPager";
import { SortTh, sortRows, useTableSort } from "../components/tableSort";
import { useCompany } from "../company/CompanyProvider";
import { PaymentLineActions } from "../features/payments/PaymentLineActions";
import { asPayment, payMethodLabel, payStatusClass, payStatusLabel, type Payment, type PaymentStatus } from "../features/payments/types";
import { amountMatches } from "../features/quotes/format";
import { localDay } from "../features/quotes/docFilters";
import { QuotesListBar } from "../features/quotes/QuotesListBar";
import { formatsFromCompany } from "../formats/types";
import { useFormatters } from "../formats/useFormatters";
import "../features/clients/tables.css";

type PaymentsPageProps = {
  companyId: number;
  /** Ouvert depuis « Encaissé ce mois » : seulement les paiements du mois en cours. */
  focusMonth?: boolean;
  onOpenClient?: (clientId: number) => void;
  onOpenInvoice?: (id: number) => void;
};

type GcTrack = {
  id: number;
  document_id: number;
  amount_cents: number;
  status_label?: string;
  open?: boolean;
  failed?: boolean;
  paid?: boolean;
  gc_payment_id?: string | null;
  document_number?: string | null;
  client_id?: number;
  client_name?: string | null;
  created_at?: string | null;
  charge_date?: string | null;
};

function trackPayment(charge: GcTrack): Payment | null {
  if (charge.paid || (!charge.open && !charge.failed)) return null;
  const ref = (charge.gc_payment_id || "").trim();
  if (!ref) return null;
  const status: PaymentStatus = charge.failed ? "failed" : "pending";
  return {
    id: -Math.abs(Number(charge.id) || 1),
    document_id: Number(charge.document_id) || 0,
    document_number: charge.document_number || null,
    client_id: Number(charge.client_id) || 0,
    client_name: charge.client_name || null,
    paid_at: (charge.created_at || "").slice(0, 10),
    amount_cents: Number(charge.amount_cents) || 0,
    refunded_cents: 0,
    applied_cents: 0,
    method: "sepa",
    method_label: "Prélèvement SEPA",
    reference: ref,
    note: "GoCardless",
    charge_on: (charge.charge_date || "").slice(0, 10) || null,
    status,
    status_label: status === "pending" ? "En attente" : charge.status_label || "Échoué",
    track_only: true,
  };
}

export function PaymentsPage({ companyId, focusMonth, onOpenClient, onOpenInvoice }: PaymentsPageProps) {
  const { company } = useCompany();
  const { money, day } = useFormatters();
  const decimalComma = formatsFromCompany(company).decimal_comma;
  const [rows, setRows] = useState<Payment[]>([]);
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState<"all" | "month">(focusMonth ? "month" : "all");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [menuId, setMenuId] = useState<number | null>(null);
  const sort = useTableSort<"status" | "number" | "date" | "charge" | "invoice" | "client" | "method" | "ref" | "amount" | "refund">();

  const load = useCallback(async () => {
    const [data, gc] = await Promise.all([
      facturxFetch<{ payments: Payment[] }>(`payments.php?company_id=${companyId}`),
      facturxFetch<{ charges?: GcTrack[] }>(`gocardless-charge.php?company_id=${companyId}`).catch(() => ({ charges: [] as GcTrack[] })),
    ]);
    const payments = (data.payments || []).map((row) => {
      const payment = asPayment(row);
      if (payment.method === "sepa" && !payment.charge_on) payment.charge_on = payment.paid_at;
      return payment;
    });
    const known = new Set(payments.map((p) => (p.reference || "").trim()).filter(Boolean));
    const tracks = (gc.charges || [])
      .map(trackPayment)
      .filter((row): row is Payment => row !== null && !known.has(row.reference || ""));
    return [...tracks, ...payments].sort((a, b) => (b.paid_at || "").localeCompare(a.paid_at || ""));
  }, [companyId]);

  useEffect(() => {
    setQ("");
    setMenuId(null);
  }, [companyId]);

  useEffect(() => {
    let stop = false;
    load()
      .then((list) => {
        if (!stop) setRows(list);
      })
      .catch((e) => {
        if (!stop) setError(e instanceof Error ? e.message : "Erreur");
      });
    return () => {
      stop = true;
    };
  }, [companyId, load]);

  const visible = useMemo(() => {
    const today = localDay();
    const monthStart = `${today.slice(0, 8)}01`;
    const s = q.trim().toLowerCase();
    return rows.filter((p) => {
      if (period === "month") {
        const at = (p.paid_at || "").slice(0, 10);
        if (!at || at < monthStart || at > today) return false;
      }
      if (!s) return true;
      return (
        [p.number, p.document_number, p.client_name, payMethodLabel(p), p.reference, p.note, payStatusLabel(p)].some((v) =>
          (v || "").toLowerCase().includes(s),
        ) || amountMatches(p.amount_cents, q)
      );
    });
  }, [rows, q, period]);

  const shown = useMemo(() => {
    if (!sort.key) return visible;
    const kind = sort.key === "date" || sort.key === "charge" ? "date" : sort.key === "amount" || sort.key === "refund" ? "num" : "text";
    return sortRows(visible, sort.dir, kind, (p) => {
      if (sort.key === "status") return payStatusLabel(p);
      if (sort.key === "number") return p.number || "";
      if (sort.key === "date") return p.paid_at || "";
      if (sort.key === "charge") return p.charge_on || "";
      if (sort.key === "invoice") return p.document_number || "";
      if (sort.key === "client") return p.client_name || "";
      if (sort.key === "method") return payMethodLabel(p);
      if (sort.key === "ref") return p.reference || "";
      if (sort.key === "amount") return p.amount_cents;
      return p.refunded_cents > 0 ? p.refunded_cents : null;
    });
  }, [visible, sort.key, sort.dir]);
  const paged = usePagedRows(shown);

  function patchRow(next: Payment) {
    setRows((list) => list.map((p) => (p.id === next.id ? next : p)));
  }

  return (
    <div className="w-full">
      {error ? <p className="mb-3 rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800">{error}</p> : null}
      {notice ? <p className="mb-3 rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{notice}</p> : null}
      <div className="fx-page">
        <PageHeader title="Paiements" />
        <QuotesListBar
          q={q}
          onQ={setQ}
          count={visible.length}
          unit="paiements"
          filters={[
            { id: "all", label: "Tous" },
            { id: "month", label: "Ce mois" },
          ]}
          filter={period}
          onFilter={(id) => setPeriod(id === "month" ? "month" : "all")}
        />
        {visible.length === 0 ? (
          <div className="fx-clients-wrap">
            <p className="fx-clients-empty">
              {q.trim() || period === "month" ? "Aucun paiement ne correspond." : "Aucun paiement enregistré."}
            </p>
          </div>
        ) : (
          <div className="fx-clients-wrap">
            <table className="fx-clients-table">
              <thead>
                <tr>
                  <SortTh label="Statut" active={sort.key === "status"} dir={sort.dir} onSort={() => sort.onSort("status", "text")} />
                  <SortTh label="N°" active={sort.key === "number"} dir={sort.dir} onSort={() => sort.onSort("number", "text")} />
                  <SortTh label="Date" active={sort.key === "date"} dir={sort.dir} onSort={() => sort.onSort("date", "date")} />
                  <SortTh label="Prélèvement" active={sort.key === "charge"} dir={sort.dir} onSort={() => sort.onSort("charge", "date")} />
                  <SortTh label="Facture" active={sort.key === "invoice"} dir={sort.dir} onSort={() => sort.onSort("invoice", "text")} />
                  <SortTh label="Client" active={sort.key === "client"} dir={sort.dir} onSort={() => sort.onSort("client", "text")} />
                  <SortTh label="Méthode" active={sort.key === "method"} dir={sort.dir} onSort={() => sort.onSort("method", "text")} />
                  <SortTh label="Référence" active={sort.key === "ref"} dir={sort.dir} onSort={() => sort.onSort("ref", "text")} />
                  <SortTh className="is-num" label="Montant" active={sort.key === "amount"} dir={sort.dir} onSort={() => sort.onSort("amount", "num")} />
                  <SortTh className="is-num" label="Remboursé" active={sort.key === "refund"} dir={sort.dir} onSort={() => sort.onSort("refund", "num")} />
                  <th className="is-act">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paged.slice.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <span className={`fx-status ${payStatusClass(p.status)}`}>{payStatusLabel(p)}</span>
                    </td>
                    <td>
                      <span className="fx-clients-table__name">{p.number || "—"}</span>
                    </td>
                    <td className="fx-clients-table__muted">{day(p.paid_at)}</td>
                    <td className="fx-clients-table__muted">{p.charge_on ? day(p.charge_on) : "—"}</td>
                    <td>
                      {onOpenInvoice ? (
                        <button type="button" className="fx-clients-table__link" onClick={() => onOpenInvoice(p.document_id)}>
                          {p.document_number || "—"}
                        </button>
                      ) : (
                        p.document_number || "—"
                      )}
                    </td>
                    <td>
                      {onOpenClient && p.client_id ? (
                        <button type="button" className="fx-clients-table__link" onClick={() => onOpenClient(p.client_id)}>
                          {p.client_name || "—"}
                        </button>
                      ) : (
                        p.client_name || "—"
                      )}
                    </td>
                    <td>{payMethodLabel(p)}</td>
                    <td className="fx-clients-table__muted">{p.reference || "—"}</td>
                    <td className="is-num">{money(p.amount_cents)}</td>
                    <td className={p.refunded_cents > 0 ? "is-num" : "is-num fx-clients-table__muted"}>
                      {p.refunded_cents > 0 ? money(p.refunded_cents) : "—"}
                    </td>
                    <td className="is-act">
                      {p.track_only ? null : (
                      <PaymentLineActions
                        row={p}
                        decimalComma={decimalComma}
                        busy={busy}
                        open={menuId === p.id}
                        onOpenChange={(next) => setMenuId(next ? p.id : null)}
                        onBusy={setBusy}
                        onError={setError}
                        onNotice={setNotice}
                        onChanged={patchRow}
                      />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ListPager page={paged.page} pages={paged.pages} total={paged.total} from={paged.from} to={paged.to} setPage={paged.setPage} />
          </div>
        )}
      </div>
    </div>
  );
}
